import React, { useEffect, useState, useRef } from 'react';
import { BookOpenIcon, CheckCircle2Icon, FileTextIcon, LockIcon, ShieldCheckIcon, SparklesIcon, StarIcon, ZapIcon } from 'lucide-react';
const shortTestimonials = [{
  quote: 'I used to rewatch videos before every session just to remember the order. Now I open the volume, pick the protocol and start. My clients notice the difference.',
  name: 'Sarah Mitchell',
  role: 'Independent wood therapist, Austin, TX',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/07ab51ae-9543-44e3-8944-d34c82efd546.jpg'
}, {
  quote: 'My clients started asking whether I offered packages. This quarter I sold six 8-session programs.',
  name: 'Megan Doyle',
  role: 'Wellness studio owner, Manchester, UK',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/6158ef13-2a07-4e75-bc3c-12db0ad4c7bb.jpg'
}, {
  quote: 'The lymphatic and postpartum volume alone is worth the full price. It is the most requested service on my calendar.',
  name: 'Nicole Barnes',
  role: 'Body therapist, Toronto, Canada',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/dc28e008-1217-4365-b0f4-613d9c388743.jpg'
}];
export function Hero() {
  const ctaAnchorRef = useRef<HTMLDivElement>(null);
  const [showStickyCta, setShowStickyCta] = useState(true);
  useEffect(() => {
    const ctaAnchor = ctaAnchorRef.current;
    if (!ctaAnchor) return;
    const observer = new IntersectionObserver(([entry]) => {
      const hasNotReachedInlineCta = !entry.isIntersecting && entry.boundingClientRect.top > 0;
      setShowStickyCta(hasNotReachedInlineCta);
    }, {
      threshold: 0.15
    });
    observer.observe(ctaAnchor);
    return () => observer.disconnect();
  }, []);
  return <>
      <header className="w-full bg-cream px-4 pt-4 sm:px-6" data-id="element-57" data-mp="2hlj8g19kaz7a">
        <div className="mx-auto flex max-w-6xl items-center justify-between border-b border-brand-100 pb-3" data-id="element-58" data-mp="2ljgzzaqt1xdu">
          <p className="text-xs font-extrabold tracking-[0.13em] text-brand-900" data-id="element-59" data-mp="s3mb9x0ezovq">
            WOOD THERAPY PRO MEGAPACK™
          </p>
          <span className="hidden text-xs font-semibold text-brand-600 sm:block" data-id="element-60" data-mp="ic86patlvyvq">
            Professional material in English
          </span>
        </div>
      </header>

      <main data-id="element-61" data-mp="deyk02xlq1bl">
        <section className="w-full bg-cream px-4 pb-7 pt-5 sm:px-6 md:pb-12 md:pt-8" data-id="element-62" data-mp="dbapb0gf78va">
          <div className="mx-auto max-w-3xl" data-id="element-63" data-mp="1b04a0ydokp1h">
            <div className="text-center" data-id="element-64" data-mp="161b0fh1ili6l">
              <div className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/10 px-3 py-1.5 text-[11px] font-bold leading-tight text-brand-700 sm:text-xs" data-id="element-65" data-mp="162m0p6vr47sy">
                <SparklesIcon className="h-3.5 w-3.5 text-accent" aria-hidden="true" data-id="element-66" data-mp="x0l50eg61tte" />
                Chosen by wellness professionals across the English-speaking
                world
              </div>

              <h1 className="mt-4 text-[2.15rem] font-bold leading-[1.03] tracking-tight text-brand-900 sm:text-5xl" data-id="element-67" data-mp="1x9krqeihcqdi">
                You already know the technique.
                <span className="block italic text-brand-600" data-id="element-68" data-mp="218tq5wogtcfe">
                  What you are missing is the session map.
                </span>
              </h1>

              <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-gray-600 sm:text-base md:text-lg" data-id="element-69" data-mp="2b1hkmkrz34ay">
                The complete system for the professional wood therapist — 6
                ready-to-use volumes, protocols by body area, client forms,
                scripts and a full business system.{' '}
                <strong className="text-brand-900" data-id="element-70" data-mp="2p1a3xv166i8w">All done for you.</strong>
              </p>

              <div className="mt-4 flex flex-wrap justify-center gap-x-3 gap-y-2 text-xs font-semibold text-brand-800" data-id="element-71" data-mp="2tyshxzu1nnnr">
                <span className="flex items-center gap-1" data-id="element-72" data-mp="uou53fape6e2">
                  <BookOpenIcon className="h-4 w-4 text-brand-500" data-id="element-73" data-mp="zmc3wcv0b58c" />6 Volumes
                </span>
                <span className="flex items-center gap-1" data-id="element-74" data-mp="4a2snewu8zwz">
                  <SparklesIcon className="h-4 w-4 text-brand-500" data-id="element-75" data-mp="3vhxffh69x3xh" />5 Bonuses
                </span>
                <span className="flex items-center gap-1" data-id="element-76" data-mp="e9aaofeipiqf">
                  <FileTextIcon className="h-4 w-4 text-brand-500" data-id="element-77" data-mp="e7hb1k2bnyrx" />
                  300+ pages
                </span>
                <span className="flex items-center gap-1" data-id="element-78" data-mp="3hqa8mnepgd0g">
                  <CheckCircle2Icon className="h-4 w-4 text-brand-500" data-id="element-79" data-mp="3hqsdg8ffnzjz" />8 tools
                </span>
              </div>

              <div className="mx-auto mt-5 w-full max-w-md" data-id="element-80" data-mp="3cp32stw4hnwf">
                <img src="https://cdn.magicpatterns.com/uploads/8U48fKTRxNnpTgaKCD9DzM/hf_20260709_234754_2ec4c90d-3b24-46e7-b38c-c9af1900736c.png" alt="Wood Therapy Pro MegaPack with six volumes and bonuses included" className="w-full rounded-xl border border-brand-100 bg-white shadow-xl shadow-brand-900/10" data-id="element-81" data-mp="3rh55elpkuv05" />
              </div>

              <div ref={ctaAnchorRef} className="mx-auto mt-5 w-full max-w-md md:mt-6" data-id="element-82" data-mp="19hvo6u3huzax">
                <a href="#pricing" className="flex w-full items-center justify-center rounded-lg bg-brand-600 px-5 py-4 text-sm font-bold text-white shadow-lg shadow-brand-900/15 transition-colors hover:bg-brand-700 focus:outline-none focus:ring-4 focus:ring-brand-500/30" data-id="element-83" data-mp="1ebglejx9jp92">
                  I WANT MY MEGAPACK NOW
                </a>
              </div>

              <div className="mt-3 flex flex-wrap justify-center gap-x-3 gap-y-1.5 text-[11px] font-medium text-gray-500" data-id="element-84" data-mp="1x5vlydz83pz0">
                <span className="flex items-center gap-1" data-id="element-85" data-mp="1n9ivrq2vzu90">
                  <LockIcon className="h-3.5 w-3.5" data-id="element-86" data-mp="1nc525me1k3pn" />
                  Secure payment
                </span>
                <span className="flex items-center gap-1" data-id="element-87" data-mp="7vo4ryg6itqz">
                  <ZapIcon className="h-3.5 w-3.5" data-id="element-88" data-mp="bvoifgvgf0wz" />
                  Instant access
                </span>
                <span className="flex items-center gap-1" data-id="element-89" data-mp="2eh0z9ywhy907">
                  <ShieldCheckIcon className="h-3.5 w-3.5" data-id="element-90" data-mp="29hxt2b4s5wqn" />
                  7-day guarantee
                </span>
                <span data-id="element-91" data-mp="24qphp9jw41au">100% PDF</span>
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-brand-100 bg-white px-4 py-5 sm:px-6" data-id="element-92" data-mp="1zrweqb28bsox">
          <div className="mx-auto grid max-w-6xl grid-cols-2 gap-4 text-center md:grid-cols-4" data-id="element-93" data-mp="1vrch76dqun29">
            {[['+800', 'professionals'], ['6 volumes + 5', 'bonuses included'], ['4.9/5', 'average rating'], ['Worldwide', 'English-speaking']].map(([value, label]) => <div key={label} className="border-brand-100 even:border-l md:border-l" data-id="element-94" data-mp="1gvmrax5ftps9">
                <p className="text-lg font-black text-brand-900 sm:text-xl" data-id="element-95" data-mp="3b82vlseoatuh">
                  {value}
                </p>
                <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500" data-id="element-96" data-mp="3jpn0ebqdkrup">
                  {label}
                </p>
              </div>)}
          </div>
        </section>

        <section className="bg-white px-4 py-10 sm:px-6 md:py-16" data-id="element-97" data-mp="3sigjylcuuded">
          <div className="mx-auto max-w-6xl" data-id="element-98" data-mp="1bdrlqla612k">
            <div className="mb-7 text-center" data-id="element-99" data-mp="1accnb158ufk">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-brand-600" data-id="element-100" data-mp="30q95nrktnd35">
                Professional experiences
              </p>
              <h2 className="mt-2 text-2xl font-black text-brand-900 md:text-3xl" data-id="element-101" data-mp="34s2xdad1h6r6">
                From improvising to a clear protocol
              </h2>
            </div>
            <div className="grid gap-4 md:grid-cols-3" data-id="element-102" data-mp="3eqs7ggyp1mwv">
              {shortTestimonials.map(testimonial => <article key={testimonial.name} className="flex flex-col rounded-2xl border border-brand-100 bg-cream p-5" data-id="element-103" data-mp="oznbuo2uigrl">
                  <div className="flex gap-0.5 text-accent" aria-label="5 out of 5 stars" data-id="element-104" data-mp="1f2sn2q655yal">
                    {[1, 2, 3, 4, 5].map(star => <StarIcon key={star} className="h-4 w-4 fill-current" aria-hidden="true" data-id="element-105" data-mp="1ouf2kypiijj5" />)}
                  </div>
                  <blockquote className="mt-3 text-sm leading-relaxed text-gray-700" data-id="element-106" data-mp="1ytnu7kwwnm2c">
                    “{testimonial.quote}”
                  </blockquote>
                  <footer className="mt-auto flex items-center gap-3 border-t border-brand-100 pt-4" data-id="element-107" data-mp="27r6j50do6kji">
                    <img src={testimonial.image} alt={testimonial.name} className="h-11 w-11 shrink-0 rounded-full border border-brand-100 object-cover" data-id="element-108" data-mp="22u7swsx4mxql" />
                    <div className="min-w-0" data-id="element-109" data-mp="8h87uk9z9bbx">
                      <p className="text-sm font-bold text-brand-900" data-id="element-110" data-mp="2k27awgv6hinm">
                        {testimonial.name}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-111" data-mp="2p0s8s9myjq8w">
                        {testimonial.role}
                      </p>
                    </div>
                  </footer>
                </article>)}
            </div>
          </div>
        </section>
      </main>

      <div className={`pointer-events-none fixed inset-x-0 bottom-0 z-50 px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-3 transition-[opacity,transform] duration-300 ease-out motion-reduce:transition-none ${showStickyCta ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0'}`} aria-hidden={!showStickyCta} data-id="element-112" data-mp="21aegci0gnpds">
        <a href="#pricing" tabIndex={showStickyCta ? 0 : -1} className="pointer-events-auto mx-auto flex w-full max-w-md items-center justify-center rounded-lg bg-brand-600 px-5 py-4 text-center text-sm font-bold text-white shadow-xl shadow-brand-900/20 transition-colors hover:bg-brand-700 focus:outline-none focus:ring-4 focus:ring-brand-500/30" data-id="element-113" data-mp="3mpc5uqmnbbck">
          I WANT MY MEGAPACK NOW
        </a>
      </div>
    </>;
}